import { useState, useRef, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import type { FileEntry } from '../types';

interface FileTransferProps {
  fileTransferPolicy: 'upload_only' | 'full';
  sessionActive: boolean;
  onClose: () => void;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

export function FileTransfer({ fileTransferPolicy, sessionActive, onClose }: FileTransferProps) {
  const [currentPath, setCurrentPath] = useState('/home/kasm-user/Desktop');
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [isCuiUpload, setIsCuiUpload] = useState(false);
  const [uploadSource, setUploadSource] = useState('');
  const [uploadNote, setUploadNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canDownload = fileTransferPolicy === 'full' && sessionActive;

  const fetchFiles = useCallback(async (path: string) => {
    if (!canDownload) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.listFiles(path);
      setFiles(result.files || []);
      setCurrentPath(result.path);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to list files');
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [canDownload]);

  // Fetch file list on mount and when canDownload
  useEffect(() => {
    if (canDownload) {
      fetchFiles(currentPath);
    }
  }, [canDownload]); // eslint-disable-line react-hooks/exhaustive-deps

  const queueFile = (file: File) => {
    setPendingFile(file);
    setIsCuiUpload(false);
    setUploadSource('');
    setUploadNote('');
    setUploadStatus(null);
    setError(null);
  };

  const handleUpload = async () => {
    if (!pendingFile) return;
    if (isCuiUpload && uploadSource.trim() === '') {
      setError('CUI uploads need a source or handling note.');
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    setUploadStatus(null);
    setError(null);
    try {
      const result = await api.uploadProfileFile(pendingFile, {
        isCui: isCuiUpload,
        source: uploadSource.trim(),
        note: uploadNote.trim(),
      }, (pct) => {
        setUploadProgress(pct);
      });
      setUploadStatus(`Uploaded ${result.filename}`);
      setPendingFile(null);
      setIsCuiUpload(false);
      setUploadSource('');
      setUploadNote('');
      // Refresh file list if viewing the Desktop
      if (canDownload && currentPath === '/home/kasm-user/Desktop') {
        fetchFiles(currentPath);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) queueFile(file);
    // Reset so same file can be re-selected
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) queueFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleEntryClick = (entry: FileEntry) => {
    if (entry.is_dir) {
      fetchFiles(entry.path);
    } else if (canDownload) {
      const url = api.getDownloadUrl(entry.path);
      window.open(url, '_blank');
    }
  };

  const navigateToPath = (path: string) => {
    fetchFiles(path);
  };

  // Build breadcrumb segments from current path
  const breadcrumbs = (() => {
    const base = '/home/kasm-user/Desktop';
    const relative = currentPath.slice(base.length);
    const segments: { name: string; path: string }[] = [
      { name: 'Desktop', path: base },
    ];
    if (relative) {
      const parts = relative.split('/').filter(Boolean);
      let accumulated = base;
      for (const part of parts) {
        accumulated += '/' + part;
        segments.push({ name: part, path: accumulated });
      }
    }
    return segments;
  })();

  return (
    <div className="file-transfer-panel">
      <div className="clipboard-panel-header">
        <span>Files</span>
        <button className="clipboard-close" onClick={onClose}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Upload Section */}
      <div className="clipboard-section">
        <label className="clipboard-section-label">Upload</label>
        <div
          className={`file-drop-zone ${dragOver ? 'drag-over' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginBottom: 6 }}>
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          <div>Drop file here or click to browse</div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
        {pendingFile && (
          <div className="file-upload-review">
            <div className="file-upload-review-name">
              <span>{pendingFile.name}</span>
              <strong>{formatSize(pendingFile.size)}</strong>
            </div>
            <label className="file-cui-toggle">
              <input
                type="checkbox"
                checked={isCuiUpload}
                onChange={(e) => setIsCuiUpload(e.target.checked)}
                disabled={uploading}
              />
              This file contains CUI
            </label>
            {isCuiUpload && (
              <textarea
                className="file-upload-source"
                value={uploadSource}
                onChange={(e) => setUploadSource(e.target.value)}
                placeholder="Source and handling note"
                disabled={uploading}
                rows={3}
              />
            )}
            <textarea
              className="file-upload-source"
              value={uploadNote}
              onChange={(e) => setUploadNote(e.target.value)}
              placeholder="Optional upload note"
              disabled={uploading}
              rows={2}
            />
            <div className="file-upload-actions">
              <button type="button" onClick={() => setPendingFile(null)} disabled={uploading}>
                Cancel
              </button>
              <button type="button" onClick={handleUpload} disabled={uploading || (isCuiUpload && uploadSource.trim() === '')}>
                Upload
              </button>
            </div>
          </div>
        )}
        {uploading && (
          <div className="file-upload-progress">
            <div className="file-upload-progress-fill" style={{ width: `${uploadProgress}%` }} />
          </div>
        )}
        {uploadStatus && <div className="file-upload-status">{uploadStatus}</div>}
        {error && <div className="file-upload-error">{error}</div>}
      </div>

      {/* File Browser — only when policy allows download */}
      {canDownload && (
        <>
          <div className="file-breadcrumb">
            {breadcrumbs.map((seg, i) => (
              <span key={seg.path}>
                {i > 0 && <span className="file-breadcrumb-separator"> / </span>}
                <button
                  className="file-breadcrumb-segment"
                  onClick={() => navigateToPath(seg.path)}
                >
                  {seg.name}
                </button>
              </span>
            ))}
          </div>
          <div className="file-list">
            {loading ? (
              <div className="file-list-loading">Loading...</div>
            ) : files.length === 0 ? (
              <div className="file-list-loading">Empty directory</div>
            ) : (
              files.map((entry) => (
                <button
                  key={entry.path + entry.name}
                  className="file-entry"
                  onClick={() => handleEntryClick(entry)}
                >
                  <span className={`file-entry-icon ${entry.is_dir ? 'dir' : ''}`}>
                    {entry.is_dir ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                    )}
                  </span>
                  <span className="file-entry-name">{entry.name}</span>
                  {!entry.is_dir && (
                    <span className="file-entry-size">{formatSize(entry.size)}</span>
                  )}
                </button>
              ))
            )}
          </div>
        </>
      )}

      <div className="clipboard-hint">
        {canDownload
          ? 'Click a file to download. Uploads appear on your persistent Desktop.'
          : sessionActive
            ? 'Uploaded files appear on your persistent Desktop.'
            : 'Upload-only mode. Files appear on your Desktop next time the VDI starts.'}
      </div>
    </div>
  );
}
